import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import AuthModal from '@/components/auth/AuthModal'
import AdminBadge from '@/components/ui/AdminBadge'
import { signInWithSocialProvider } from '@/lib/socialAuth'

export default function Header() {
  const {
    user,
    signOut,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    isAdmin,
    devLogin
  } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [searchText, setSearchText] = useState(searchParams.get('q') || '')
  const [isFocused, setIsFocused] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Check if we're on the marketplace page
  const isMarketplace = location.pathname === '/marketplace'

  // Check if we're in development mode
  const isDev = import.meta.env.DEV

  // Update search text when URL params change
  useEffect(() => {
    setSearchText(searchParams.get('q') || '')
  }, [searchParams])

  // Update URL params when search text changes
  useEffect(() => {
    if (!isMarketplace) return

    const timer = setTimeout(() => {
      if (searchText) {
        setSearchParams({ q: searchText })
      } else {
        setSearchParams({})
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchText, isMarketplace, setSearchParams])

  const handleLogout = () => {
    signOut()
    setShowUserDropdown(false)
  }

  const handleProfileClick = () => {
    navigate('/profile')
    setShowUserDropdown(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLogin = async (email: string, password: string) => {
    await signIn({ email, password })
  }

  const handleRegister = async (email: string, password: string, name: string) => {
    const [firstName, ...lastNameParts] = name.split(' ')
    await signUp({
      email,
      password,
      firstName,
      lastName: lastNameParts.join(' ')
    })
  }

  const handleSocialLogin = async (provider: 'google' | 'apple', credential?: string) => {
    try {
      await signInWithSocialProvider(
        provider,
        { signInWithGoogle, signInWithApple },
        credential
      )
    } catch (error) {
      console.error(`${provider} login error:`, error)
      throw error
    }
  }


  return (
    <header className="glass-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/marketplace" className="flex items-center gap-2 no-underline flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-brand-primary flex items-center justify-center text-white shadow-md">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zM10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
            </div>
            <span className="text-lg font-bold gradient-text">certi</span>
          </Link>

          {/* Search Bar - Only show on marketplace */}
          {isMarketplace && (
            <div className="flex-1 max-w-2xl">
              <div
                className={`flex items-center rounded-lg border-2 px-3 py-2 transition-colors ${
                  isFocused ? 'border-brand-primary' : ''
                }`}
                style={{
                  backgroundColor: 'var(--surface-primary)',
                  borderColor: isFocused ? 'var(--brand-primary)' : 'var(--border-primary)'
                }}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  style={{ color: 'var(--text-muted)' }}
                >
                  <circle cx={11} cy={11} r={8} />
                  <path d="m21 21-4.35-4.35" />
                </svg>

                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder="Search by player, team, or item…"
                  className="flex-1 outline-none text-sm"
                  style={{
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-shrink-0">
            {user && isAdmin() && <AdminBadge />}
            <button
              onClick={() => navigate('/favorites')}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              title="My Favorites"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </button>
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-1 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title={user.firstName || 'User menu'}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                
                {showUserDropdown && (
                  <div
                    className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50"
                    style={{
                      backgroundColor: 'var(--surface-primary)',
                      borderColor: 'var(--border-primary)',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <div className="py-2">
                      <button
                        onClick={handleProfileClick}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-opacity-80 transition-colors"
                        style={{ 
                          color: 'var(--text-primary)',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--surface-secondary)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        Your Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-opacity-80 transition-colors"
                        style={{ 
                          color: 'var(--text-primary)',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--surface-secondary)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {isDev && (
                  <button
                    onClick={() => devLogin()}
                    className="px-3 py-1 text-xs rounded-md bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition-colors"
                    title="Dev Login (Dev Mode Only)"
                  >
                    Dev Login
                  </button>
                )}
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Sign in"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onSocialLogin={handleSocialLogin}
      />
    </header>
  )
}
